import type { Promocode } from '@@partner:types:app'
import type { GQL_ID } from '@@partner:types:graphql'

import { mergeLeft } from 'merge-left-utils'
import { modifyTime } from '@@partner:helpers/date-utils'

export interface PromocodeFormState {
  id?: GQL_ID
  code: string
  activeFrom?: Date
  activeUntil?: Date
  enabled: boolean
  usesCount: number

  deliveryDiscount: number
  deliveryDiscountInPercent: boolean
  cartDiscount: number
  cartDiscountInPercent: boolean

  maxOrderDiscount: number
  maxOrderDiscountInPercent: boolean

  firstDelivery: boolean
  minCartAmount: number
  activationLimit: number
  noActivationLimit: boolean
  userActivationLimit: number
  noUserActivationLimit: boolean
}

export const PROMOCODE_DEFAULT_STATE: PromocodeFormState = {
  id: undefined,
  code: '',
  enabled: false,
  usesCount: 0,
  deliveryDiscount: 0,
  deliveryDiscountInPercent: false,
  cartDiscount: 0,
  cartDiscountInPercent: false,
  firstDelivery: false,
  minCartAmount: 0,
  maxOrderDiscount: 0,
  maxOrderDiscountInPercent: false,
  activationLimit: 0,
  noActivationLimit: false,

  userActivationLimit: 0,
  noUserActivationLimit: true,

  activeFrom: undefined,
  activeUntil: undefined
}

export const makePromocodeFormState = (
  promocode?: Promocode
): PromocodeFormState => {
  return mergeLeft<PromocodeFormState>(PROMOCODE_DEFAULT_STATE, promocode)
}

// state and api variable are same excluding info fields
// so drop them and go
export const mapStateToApiVariable = (state: PromocodeFormState): any => ({
  id: state.id,
  code: state.code.trim().toUpperCase(),
  is_active: state.enabled,
  activated_count: state.usesCount,
  // discount_in_percents: state.
  for_first_delivery_only: state.firstDelivery,
  max_customer_activation_count: state.noUserActivationLimit
    ? -1
    : Number(state.userActivationLimit),
  max_total_activation_count: state.noActivationLimit
    ? -1
    : Number(state.activationLimit),
  min_order_total: Number(state.minCartAmount),
  discount_in_percents: state.cartDiscountInPercent,
  delivery_discount_in_percent: state.deliveryDiscountInPercent,
  max_order_discount_in_percent: state.maxOrderDiscountInPercent,
  max_order_discount: Number(state.maxOrderDiscount),
  cart_total_discount: Number(state.cartDiscount),
  delivery_discount: Number(state.deliveryDiscount),
  active_from: state.activeFrom
    ? modifyTime(state.activeFrom as Date, 0, 0, 0).getTime()
    : null,
  active_until: state.activeUntil
    ? modifyTime(state.activeUntil as Date, 23, 59, 59).getTime()
    : null
})

export const mapApiDataToState = (data: Promocode): PromocodeFormState => ({
  id: data.id,
  enabled: data.is_active,
  code: data.code,
  activeFrom: data.active_from ? new Date(data.active_from) : undefined,
  activeUntil: data.active_until ? new Date(data.active_until) : undefined,
  usesCount: Number(data.activated_count),
  activationLimit: Number(data.max_total_activation_count),
  deliveryDiscount: Number(data.delivery_discount),
  deliveryDiscountInPercent: data.delivery_discount_in_percent,
  cartDiscount: Number(data.cart_total_discount),
  cartDiscountInPercent: data.discount_in_percents,
  maxOrderDiscount: Number(data.max_order_discount),
  maxOrderDiscountInPercent: data.max_order_discount_in_percent,
  firstDelivery: data.for_first_delivery_only,
  minCartAmount: Number(data.min_order_total),

  noActivationLimit: Number(data.max_total_activation_count) === -1,
  noUserActivationLimit: Number(data.max_customer_activation_count) === -1,
  userActivationLimit: data.max_customer_activation_count
})
